/**
 * useMessageSubscription
 *
 * Real-time message subscription via WebSocket.
 * Replaces Firebase Firestore subscriptions.
 *
 * Usage:
 *   const { isConnected, pendingMessages, flush } = useMessageSubscription({
 *     orgId: 'org-123',
 *     channelIds: ['ch1', 'ch2'],
 *     onMessage: (msg) => console.log('New message:', msg),
 *   });
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { OrgMessage } from '../../common/entities';
import VoiceBrainAPIClient from '../../api/voicebrain-api';
import { logger } from '../../utils/logger';
import { fetchWsToken, isAuthInvalidated, onAuthRecovery } from '../services/auth';

interface UseMessageSubscriptionOptions {
  /** Organization ID to subscribe to */
  orgId: string;
  /** Optional channel IDs to filter (empty = all channels) */
  channelIds?: string[];
  /** Callback for new messages */
  onMessage?: (message: OrgMessage) => void;
  /** Whether subscription is enabled (default: true) */
  enabled?: boolean;
  /** Buffer messages instead of firing immediately (for queue mode) */
  bufferMode?: boolean;
}

interface UseMessageSubscriptionResult {
  /** WebSocket connection status */
  isConnected: boolean;
  /** Connection error (if any) */
  error: string | null;
  /** Pending messages (when bufferMode is true) */
  pendingMessages: OrgMessage[];
  /** Flush pending messages and clear buffer */
  flush: () => OrgMessage[];
  /** Clear pending messages without returning them */
  clearBuffer: () => void;
  /** Manually update channel filter */
  updateChannels: (channelIds: string[]) => void;
}

const RECONNECT_DELAYS = [1000, 2000, 5000, 10000, 30000]; // Exponential backoff

export function useMessageSubscription({
  orgId,
  channelIds = [],
  onMessage,
  enabled = true,
  bufferMode = false
}: UseMessageSubscriptionOptions): UseMessageSubscriptionResult {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingMessages, setPendingMessages] = useState<OrgMessage[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const channelIdsRef = useRef(channelIds);
  const bufferModeRef = useRef(bufferMode);

  const onMessageRef = useRef(onMessage);

  // Keep refs in sync
  useEffect(() => {
    channelIdsRef.current = channelIds;
  }, [channelIds]);

  useEffect(() => {
    bufferModeRef.current = bufferMode;
  }, [bufferMode]);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  // Flush pending messages
  const flush = useCallback((): OrgMessage[] => {
    const messages = [...pendingMessages];
    setPendingMessages([]);
    return messages;
  }, [pendingMessages]);

  // Clear buffer without returning
  const clearBuffer = useCallback(() => {
    setPendingMessages([]);
  }, []);

  // Update channel filter
  const updateChannels = useCallback((newChannelIds: string[]) => {
    channelIdsRef.current = newChannelIds;
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'update_channels',
          channelIds: newChannelIds
        })
      );
    }
  }, []);

  // Handle incoming message — uses ref so WS handler always sees latest callback
  // without causing reconnections when the callback identity changes
  const handleMessage = useCallback((message: OrgMessage) => {
    if (bufferModeRef.current) {
      setPendingMessages((prev) => {
        if (prev.some((m) => m.talk_id === message.talk_id)) return prev;
        return [message, ...prev];
      });
    } else if (onMessageRef.current) {
      onMessageRef.current(message);
    }
  }, []);

  // Connect WebSocket
  const connect = useCallback(async () => {
    if (!enabled || !orgId) return;

    // Don't try to connect if auth is invalidated
    if (isAuthInvalidated()) {
      logger.debug('[ws] Auth is invalidated, not attempting connection');
      return;
    }

    try {
      const token = await fetchWsToken();
      if (!token) {
        // If auth is invalidated, don't throw/retry
        if (isAuthInvalidated()) {
          logger.debug('[ws] Auth is invalidated, not scheduling retry');
          return;
        }
        throw new Error('Failed to fetch WS token');
      }

      const rawApiUrl = import.meta.env.PROD
        ? import.meta.env.VITE_VB_SERVER_URL || VoiceBrainAPIClient.getURI()
        : VoiceBrainAPIClient.getURI();
      const baseUrl = rawApiUrl.replace(/\/v3\/?$/, '').replace(/^http/, 'ws');
      const channelParam =
        channelIdsRef.current.length > 0 ? `&channel_ids=${channelIdsRef.current.join(',')}` : '';
      const wsUrl = `${baseUrl}/ws/messages?org_id=${orgId}${channelParam}`;
      const subprotocols = ['vb-ws-token', token];

      logger.info('[ws] Connecting to:', wsUrl, '(protocol masked)');

      const ws = new WebSocket(wsUrl, subprotocols);
      wsRef.current = ws;

      ws.onopen = () => {
        logger.info('[ws] Connected');
        setIsConnected(true);
        setError(null);
        reconnectAttempt.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'message') {
            handleMessage(data.data);
          } else if (data.type === 'connected') {
            logger.info('[ws] Subscription confirmed:', data.data);
          } else if (data.type === 'pong') {
            // Heartbeat response
          }
        } catch (err) {
          logger.error('[ws] Failed to parse message:', err);
        }
      };

      ws.onclose = (event) => {
        logger.warn('[ws] Connection closed:', event.code, event.reason);
        setIsConnected(false);
        wsRef.current = null;

        // Don't reconnect if auth is invalidated
        if (event.code !== 1000 && enabled && !isAuthInvalidated()) {
          const delay =
            RECONNECT_DELAYS[Math.min(reconnectAttempt.current, RECONNECT_DELAYS.length - 1)];
          logger.info(`[ws] Reconnecting in ${delay}ms...`);
          reconnectTimeout.current = setTimeout(() => {
            reconnectAttempt.current++;
            connect();
          }, delay);
        }
      };

      ws.onerror = (event) => {
        logger.error('[ws] WebSocket error', event);
        setError('Connection error');
      };

      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 25000);

      ws.addEventListener('close', () => clearInterval(pingInterval));
    } catch (err) {
      logger.error('[ws] Failed to connect:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect');
    }
  }, [enabled, orgId, handleMessage]);

  // Cleanup on unmount or disable
  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current);
      reconnectTimeout.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'Component unmounting');
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Connect when enabled/orgId changes
  useEffect(() => {
    if (enabled && orgId) {
      connect();
    } else {
      disconnect();
    }
    return disconnect;
  }, [enabled, orgId, connect, disconnect]);

  // Reconnect when auth recovers (e.g., after server restart)
  useEffect(() => {
    const unsubscribe = onAuthRecovery(() => {
      logger.info('[ws] Auth recovered, reconnecting...');
      if (enabled && orgId) {
        reconnectAttempt.current = 0; // Reset backoff
        connect();
      }
    });
    return unsubscribe;
  }, [enabled, orgId, connect]);

  // Reconnect when tab becomes visible if WS died while backgrounded
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const handleVisibility = () => {
      if (
        document.visibilityState === 'visible' &&
        enabled &&
        orgId &&
        (!wsRef.current || wsRef.current.readyState > WebSocket.OPEN)
      ) {
        logger.info('[ws] Tab visible, WS dead — reconnecting');
        reconnectAttempt.current = 0;
        connect();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [enabled, orgId, connect]);

  // Update channel filter when it changes (only when enabled and connected)
  useEffect(() => {
    if (enabled && channelIds.length > 0) {
      updateChannels(channelIds);
    }
  }, [channelIds.join(','), updateChannels, enabled]);

  return {
    isConnected,
    error,
    pendingMessages,
    flush,
    clearBuffer,
    updateChannels
  };
}

export default useMessageSubscription;
